import React, { useState, useEffect } from "react";
import {
  Card,
  Form,
  Button,
  Spinner,
  Alert,
  Badge,
} from "react-bootstrap";
import api from "@/api/api";

const TestNotification = () => {
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState(null);

  // Form state
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [priority, setPriority] = useState("normal");

  useEffect(() => {
    fetchUsersWithTokens();
  }, []);

  const fetchUsersWithTokens = async () => {
    try {
      setLoading(true);
      const response = await api.get("/admin/notifications/users-with-tokens");
      if (response.data?.success) {
        setUsers(response.data.data.users);
      } else {
        setMessage({ type: "danger", text: "Foydalanuvchilarni yuklashda xatolik" });
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      setMessage({ type: "danger", text: "Foydalanuvchilarni yuklashda xatolik" });
    } finally {
      setLoading(false);
    }
  };

  const handleUserToggle = (userId) => {
    if (selectedUsers.includes(userId)) {
      setSelectedUsers(selectedUsers.filter((id) => id !== userId));
    } else {
      setSelectedUsers([...selectedUsers, userId]);
    }
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map((u) => u.id));
    }
  };

  const handleSendNotification = async () => {
    if (!title.trim()) {
      setMessage({ type: "danger", text: "Sarlavha kiritilishi shart" });
      return;
    }

    if (selectedUsers.length === 0) {
      setMessage({ type: "danger", text: "Kamida bitta foydalanuvchi tanlang" });
      return;
    }

    try {
      setSending(true);
      setMessage(null);

      const response = await api.post("/admin/notifications/send", {
        title: title.trim(),
        body: body.trim() || null,
        user_ids: selectedUsers,
        notification_type: "admin_broadcast",
        priority: priority,
      });

      if (response.data) {
        setMessage({
          type: "success",
          text: `Bildirishnoma muvaffaqiyatli yuborildi! Jo'natildi: ${response.data.notifications_sent}, Xato: ${response.data.notifications_failed}`,
        });
        // Clear form
        setTitle("");
        setBody("");
        setPriority("normal");
        setSelectedUsers([]);
      }
    } catch (error) {
      console.error("Error sending notification:", error);
      setMessage({
        type: "danger",
        text: error.response?.data?.message || "Bildirishnomani yuborishda xatolik",
      });
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="content">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
          <Spinner animation="border" variant="primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="content">
      <Card>
        <Card.Header>
          <h5 className="title">Test Bildirishnoma Jo'natish</h5>
          <p className="category">FCM tokeni bor foydalanuvchilarga test bildirishnoma yuboring</p>
        </Card.Header>
        <Card.Body>
          {message && (
            <Alert variant={message.type} className="mb-4" dismissible onClose={() => setMessage(null)}>
              {message.text}
            </Alert>
          )}

          <div className="row">
            {/* Left side - Form */}
            <div className="col-md-6">
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Sarlavha <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Bildirishnoma sarlavhasi"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    maxLength={100}
                  />
                  <Form.Text className="text-muted">
                    {title.length}/100 belgi
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Matn</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    placeholder="Bildirishnoma matni (ixtiyoriy)"
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    maxLength={500}
                  />
                  <Form.Text className="text-muted">
                    {body.length}/500 belgi
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label>Muhimlik darajasi</Form.Label>
                  <Form.Select value={priority} onChange={(e) => setPriority(e.target.value)}>
                    <option value="low">📝 Past</option>
                    <option value="normal">ℹ️ Oddiy</option>
                    <option value="high">⚠️ Yuqori</option>
                    <option value="urgent">‼️ Shoshilinch</option>
                  </Form.Select>
                </Form.Group>

                <div className="d-flex gap-2">
                  <Button
                    variant="primary"
                    onClick={handleSendNotification}
                    disabled={sending || selectedUsers.length === 0 || !title.trim()}
                  >
                    {sending ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Jo'natilmoqda...
                      </>
                    ) : (
                      <>
                        <i className="nc-icon nc-send me-2" />
                        Bildirishnoma Jo'natish
                      </>
                    )}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setTitle("");
                      setBody("");
                      setPriority("normal");
                      setSelectedUsers([]);
                      setMessage(null);
                    }}
                  >
                    Tozalash
                  </Button>
                </div>
              </Form>
            </div>

            {/* Right side - User selection */}
            <div className="col-md-6">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6>
                  Foydalanuvchilar{" "}
                  <Badge bg="primary">{users.length}</Badge>
                </h6>
                <Button variant="outline-primary" size="sm" onClick={handleSelectAll}>
                  {selectedUsers.length === users.length ? "Hech birini tanlama" : "Hammasini tanla"}
                </Button>
              </div>

              {users.length === 0 ? (
                <Alert variant="warning">
                  Hech qanday foydalanuvchi FCM token'i bilan topilmadi.
                  <br />
                  <small>Foydalanuvchilar mobil ilovaga login qilishlari kerak.</small>
                </Alert>
              ) : (
                <div
                  style={{
                    maxHeight: "500px",
                    overflowY: "auto",
                    border: "1px solid #dee2e6",
                    borderRadius: "8px",
                    padding: "10px",
                  }}
                >
                  {users.map((user) => (
                    <div
                      key={user.id}
                      className={`p-3 mb-2 border rounded ${
                        selectedUsers.includes(user.id) ? "bg-light border-primary" : ""
                      }`}
                      style={{ cursor: "pointer" }}
                      onClick={() => handleUserToggle(user.id)}
                    >
                      <Form.Check
                        type="checkbox"
                        id={`user-${user.id}`}
                        checked={selectedUsers.includes(user.id)}
                        onChange={() => handleUserToggle(user.id)}
                        label={
                          <div>
                            <strong>{user.name}</strong>
                            <br />
                            <small className="text-muted">
                              {user.employee_id} • {user.login}
                              {user.phone && ` • ${user.phone}`}
                            </small>
                            <br />
                            <Badge bg="success" className="mt-1">
                              {user.fcm_token_count} token
                            </Badge>
                          </div>
                        }
                      />
                    </div>
                  ))}
                </div>
              )}

              {selectedUsers.length > 0 && (
                <div className="mt-3">
                  <Alert variant="info">
                    <strong>{selectedUsers.length}</strong> foydalanuvchi tanlangan
                  </Alert>
                </div>
              )}
            </div>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default TestNotification;
